"use client";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useEffect, useRef, useState } from "react";
import ReceivingSettings from "../../components/pages-components/checkout/checkout-helpers/receiving-settings/ReceivingSettings";
import { useAtom } from "jotai";
import { isSignedInAtom } from "@/hooks/authAtoms";
import DeliveryNavpar from "./helpers/DeliveryNavpar/DeliveryNavpar";
import { useGetProfileQuery } from "@/api/AppApi/services/auth";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import { receivingSettingsAtom } from "@/hooks/receivingHeaderAtom";
import { getSettingsFromStorage } from "@/app/components/pages-components/checkout/checkout-helpers/receiving-settings/receiving-settings-helpers/helpers";
import {
  getBranchIdCookie,
  storeBranchIdCookie,
} from "@/utils/functions/cookieOperations";
import { PagesPaths } from "@/utils/functions/pages-paths";
import { usePathname } from "next/navigation";
import { isNewHeaderAtom } from "@/hooks/layoutAtoms";
import useIsClient from "@/hooks/useIsClient";

interface props {
  mobileTop: number;
}
const DeliveryHeader: React.FC<props> = ({ mobileTop }) => {
  const hasReloadedRef = useRef(false);
  const isClient = useIsClient();
  const pathname = usePathname();
  const getCompany = useGetCompanyQuery();
  const [receivingSetting, setReceivingSetting] = useAtom(
    receivingSettingsAtom,
  );
  const settingsFromLocalStorage = getSettingsFromStorage(receivingSetting!);
  const { show_receiving_header } = getCompany?.data?.result ?? {};
  const getProfile = useGetProfileQuery();
  const profile = getProfile?.data?.result;
  const { settings } = profile ?? {};
  const [isSignedIn] = useAtom(isSignedInAtom);
  const mobile = useIsMobile();
  const [isNewHeader] = useAtom(isNewHeaderAtom);
  const top = !mobile ? (isNewHeader ? 128 : 64) : mobileTop;
  const hasReceivingType = isSignedIn
    ? !!settings?.receiving_type
    : !!receivingSetting?.receiving_type;

  // Check if current page is an auth page or not found page
  const authPages = [
    PagesPaths.signin,
    PagesPaths.register,
    PagesPaths.confirmation,
    PagesPaths.forget_password,
    PagesPaths.reset_password,
    PagesPaths.register_confirm,
    PagesPaths.password_confirm,
    PagesPaths.otp_signin,
    PagesPaths.notFound,
  ];
  const isAuthOrNotFoundPage = authPages.includes(pathname as PagesPaths);

  const [isDrawerOpen, setIsDrawerOpen] = useState(
    !hasReceivingType && !isAuthOrNotFoundPage,
  );

  const handleDrawerOpen = () => {
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  // Synchronize branch_id cookie with receiving-settings localStorage
  useEffect(() => {
    if (isSignedIn) return; // Signed-in users sync from profile settings

    const branchIdCookie = getBranchIdCookie();
    const storageBranchId = receivingSetting?.receiving_branch?.id?.toString();

    // If localStorage has a branch but cookie is missing OR different (e.g. middleware set default)
    if (storageBranchId && branchIdCookie !== storageBranchId) {
      storeBranchIdCookie(+storageBranchId);
      // Force reload so SSR runs with the correct branch ID
      window.location.reload();
    } else if (!storageBranchId && receivingSetting) {
      // Clean up stale skeleton settings that have no branch
      setReceivingSetting(null);
    }
  }, [receivingSetting, isSignedIn, setReceivingSetting]);

  useEffect(() => {
    if (!isSignedIn || !settings || hasReloadedRef.current) return;
    const { pickup_branch, receiving_type } = settings ?? {};
    const branchIdCookie = getBranchIdCookie();
    const receivingBranchId = +pickup_branch;

    const cookieBranch = branchIdCookie ? +branchIdCookie : null;

    // Sync cookie with profile settings and reload if changed
    // This ensures SSR on the next request uses the correct branch
    if (
      !!receiving_type &&
      receivingBranchId != null &&
      cookieBranch !== receivingBranchId
    ) {
      hasReloadedRef.current = true;
      storeBranchIdCookie(receivingBranchId);
      window.location.reload();
    }
  }, [isSignedIn, settings]);

  useEffect(() => {
    // Close drawer if on auth/not found pages, otherwise show it if no receiving type is set
    if (isAuthOrNotFoundPage) {
      setIsDrawerOpen(false);
    } else {
      setIsDrawerOpen(!hasReceivingType);
    }
  }, [hasReceivingType, isAuthOrNotFoundPage]);
  if (isSignedIn && getProfile.isFetching) return;
  if (!show_receiving_header || !isClient) return;
  return (
    <>
      <div style={{ height: top }} />
      <DeliveryNavpar
        top={top}
        handleDrawerOpen={handleDrawerOpen}
        mobile={mobile}
        settings={isSignedIn ? settings! : settingsFromLocalStorage}
      />
      {isDrawerOpen && (
        <ReceivingSettings
          isOpen={isDrawerOpen}
          onClose={
            isAuthOrNotFoundPage || hasReceivingType
              ? handleDrawerClose
              : undefined
          }
        />
      )}
    </>
  );
};
export default DeliveryHeader;
